import { UMAP } from 'umap-js';

interface UserVector {
  userId: string;
  vector: number[];
  subVectors: {
    v_P: number[];
    v_V: number[];
    v_L: number[];
    v_A: number[];
    v_C: number[];
  };
  metaDimensions: {
    plasticityIndex: number;
    stabilityIndex: number;
    sensoryFrictionIndex: number;
  };
}

export interface UMAPPoint {
  userId: string;
  x: number;
  y: number;
}

/**
 * Generador de números pseudoaleatorios con semilla
 * Esto garantiza que UMAP genere la misma proyección cada vez
 */
function seededRandom(seed: number) {
  let state = seed;
  return function() {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
}

/**
 * Calcula la distancia coseno entre dos vectores
 * Retorna 1 - similitud_coseno para que sea una distancia (0 = idénticos, 2 = opuestos)
 */
function cosineSimilarity(a: number[], b: number[]): number {
  if (a.length !== b.length) return 1;
  
  let dotProduct = 0;
  let normA = 0;
  let normB = 0;
  
  for (let i = 0; i < a.length; i++) {
    dotProduct += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  
  normA = Math.sqrt(normA);
  normB = Math.sqrt(normB);
  
  if (normA === 0 || normB === 0) return 1;
  
  const similarity = dotProduct / (normA * normB);
  return 1 - similarity; // Convertir similitud a distancia
}

/**
 * Función de distancia coseno para UMAP
 */
function cosineDistance(a: number[], b: number[]): number {
  return cosineSimilarity(a, b);
}

/**
 * Normaliza las coordenadas UMAP al rango [0, 1]
 */
function normalizeCoordinates(coordinates: number[][]): number[][] {
  if (coordinates.length === 0) return [];
  
  // Encontrar min y max para cada dimensión
  const xValues = coordinates.map(c => c[0]);
  const yValues = coordinates.map(c => c[1]);
  
  const minX = Math.min(...xValues);
  const maxX = Math.max(...xValues);
  const minY = Math.min(...yValues);
  const maxY = Math.max(...yValues);
  
  const rangeX = maxX - minX || 1;
  const rangeY = maxY - minY || 1;
  
  // Normalizar
  return coordinates.map(([x, y]) => [
    (x - minX) / rangeX,
    (y - minY) / rangeY
  ]);
}

/**
 * Normaliza coordenadas UMAP centrando un punto específico en (0.5, 0.5).
 * Los demás puntos se posicionan relativamente a ese centro.
 * Se escala para que todos los puntos queden dentro de [0, 1] si es posible,
 * pero se permite desbordamiento para mantener al usuario en el centro exacto.
 */
function normalizeCoordinatesCentered(
  coordinates: number[][],
  centerIndex: number
): number[][] {
  if (coordinates.length === 0 || centerIndex < 0 || centerIndex >= coordinates.length) {
    return normalizeCoordinates(coordinates);
  }

  const centerX = coordinates[centerIndex][0];
  const centerY = coordinates[centerIndex][1];

  // Trasladar para que el punto central quede en (0, 0)
  const translated = coordinates.map(([x, y]) => [x - centerX, y - centerY]);

  // Encontrar la máxima distancia absoluta desde el centro
  let maxAbsX = 0;
  let maxAbsY = 0;
  translated.forEach(([x, y]) => {
    maxAbsX = Math.max(maxAbsX, Math.abs(x));
    maxAbsY = Math.max(maxAbsY, Math.abs(y));
  });

  const maxAbs = Math.max(maxAbsX, maxAbsY) || 1;

  // Escalar para que el punto más lejano quede a 0.45 del centro (dejando margen)
  // Luego trasladar para que el centro quede en (0.5, 0.5)
  const scale = 0.45 / maxAbs;

  return translated.map(([x, y]) => [
    0.5 + x * scale,
    0.5 + y * scale
  ]);
}

/**
 * Ejecuta UMAP sobre los vectores de usuarios y retorna coordenadas 2D
 * 
 * @param userVectors - Array de vectores de usuarios construidos
 * @param nNeighbors - Número de vecinos a considerar (default: 15)
 * @param minDist - Distancia mínima entre puntos (default: 0.1)
 * @returns Array de puntos con coordenadas x,y para cada usuario
 */
export async function projectWithUMAP(
  userVectors: UserVector[],
  nNeighbors: number = 15,
  minDist: number = 0.1
): Promise<UMAPPoint[]> {
  if (userVectors.length === 0) {
    return [];
  }
  
  // Si hay muy pocos usuarios, ajustar n_neighbors
  const adjustedNeighbors = Math.min(nNeighbors, Math.max(2, userVectors.length - 1));
  
  try {
    // Extraer solo los vectores numéricos
    const vectors = userVectors.map(uv => uv.vector);
    
    // Usar semilla fija para reproducibilidad
    const randomGenerator = seededRandom(42);
    
    // Configurar UMAP con distancia coseno y semilla fija
    const umap = new UMAP({
      nComponents: 2,
      nNeighbors: adjustedNeighbors,
      minDist: minDist,
      spread: 1.0,
      random: randomGenerator,
      distanceFn: cosineDistance as any
    });
    
    // Ejecutar UMAP
    const embedding = await umap.fitAsync(vectors, (epochNumber) => {
      // Callback opcional para progreso
      // console.log(`UMAP epoch ${epochNumber}`);
    });
    
    // Normalizar coordenadas al rango [0, 1]
    const normalizedEmbedding = normalizeCoordinates(embedding);
    
    // Mapear coordenadas a usuarios
    const umapPoints: UMAPPoint[] = userVectors.map((uv, index) => ({
      userId: uv.userId,
      x: normalizedEmbedding[index][0],
      y: normalizedEmbedding[index][1]
    }));
    
    return umapPoints;
  } catch (error) {
    console.error('Error ejecutando UMAP:', error);
    return [];
  }
}

/**
 * Ejecuta UMAP de forma síncrona (para casos donde no se necesita async)
 */
export function projectWithUMAPSync(
  userVectors: UserVector[],
  nNeighbors: number = 15,
  minDist: number = 0.1
): UMAPPoint[] {
  if (userVectors.length === 0) {
    return [];
  }
  
  const adjustedNeighbors = Math.min(nNeighbors, Math.max(2, userVectors.length - 1));
  
  try {
    const vectors = userVectors.map(uv => uv.vector);
    
    // Usar semilla fija para reproducibilidad
    const randomGenerator = seededRandom(42);
    
    const umap = new UMAP({
      nComponents: 2,
      nNeighbors: adjustedNeighbors,
      minDist: minDist,
      spread: 1.0,
      random: randomGenerator,
      distanceFn: cosineDistance as any
    });
    
    // Ejecutar UMAP de forma síncrona
    const embedding = umap.fit(vectors);
    
    const normalizedEmbedding = normalizeCoordinates(embedding);
    
    const umapPoints: UMAPPoint[] = userVectors.map((uv, index) => ({
      userId: uv.userId,
      x: normalizedEmbedding[index][0],
      y: normalizedEmbedding[index][1]
    }));
    
    return umapPoints;
  } catch (error) {
    console.error('Error ejecutando UMAP:', error);
    return [];
  }
}

/**
 * Ejecuta UMAP sobre los vectores de usuarios y retorna coordenadas 2D
 * con el usuario actual centrado en el medio del mapa.
 * 
 * @param userVectors - Array de vectores de usuarios construidos
 * @param currentUserId - ID del usuario que debe quedar centrado
 * @param nNeighbors - Número de vecinos a considerar (default: 15)
 * @param minDist - Distancia mínima entre puntos (default: 0.1)
 * @returns Array de puntos con coordenadas x,y para cada usuario
 */
export async function projectWithUMAPCentered(
  userVectors: { userId: string; vector: number[]; subVectors: any; metaDimensions: any }[],
  currentUserId: string,
  nNeighbors: number = 15,
  minDist: number = 0.1
): Promise<UMAPPoint[]> {
  if (userVectors.length === 0) {
    return [];
  }

  const adjustedNeighbors = Math.min(nNeighbors, Math.max(2, userVectors.length - 1));

  try {
    const vectors = userVectors.map(uv => uv.vector);

    const randomGenerator = seededRandom(42);

    const umap = new UMAP({
      nComponents: 2,
      nNeighbors: adjustedNeighbors,
      minDist: minDist,
      spread: 1.0,
      random: randomGenerator,
      distanceFn: cosineDistance as any
    });

    const embedding = await umap.fitAsync(vectors, () => {});

    // Encontrar el índice del usuario actual
    const centerIndex = userVectors.findIndex(uv => uv.userId === currentUserId);

    // Normalizar con el usuario actual en el centro
    const normalizedEmbedding = centerIndex >= 0
      ? normalizeCoordinatesCentered(embedding, centerIndex)
      : normalizeCoordinates(embedding);

    const umapPoints: UMAPPoint[] = userVectors.map((uv, index) => ({
      userId: uv.userId,
      x: normalizedEmbedding[index][0],
      y: normalizedEmbedding[index][1]
    }));

    return umapPoints;
  } catch (error) {
    console.error('Error ejecutando UMAP centrado:', error);
    return [];
  }
}

/**
 * Calcula la distancia euclidiana entre dos puntos UMAP
 */
export function calculateDistance(point1: UMAPPoint, point2: UMAPPoint): number {
  const dx = point1.x - point2.x;
  const dy = point1.y - point2.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Encuentra los N usuarios más cercanos a un usuario dado
 */
export function findNearestNeighbors(
  targetUserId: string,
  allPoints: UMAPPoint[],
  n: number = 5
): UMAPPoint[] {
  const targetPoint = allPoints.find(p => p.userId === targetUserId);
  if (!targetPoint) return [];
  
  // Calcular distancias
  const distances = allPoints
    .filter(p => p.userId !== targetUserId)
    .map(p => ({
      point: p,
      distance: calculateDistance(targetPoint, p)
    }))
    .sort((a, b) => a.distance - b.distance)
    .slice(0, n)
    .map(d => d.point);
  
  return distances;
}
